/**
 * Hook for Cross-Section Validation — A2-compatible order
 *
 * Validates rules across patient information and health insurance card sections
 * following the EXACT same order as A2 (edit.blade.php #btn-update click handler).
 *
 * A2 steps 1-15:
 *  1. Age < 0 → ERROR
 *  2. Age < 70 + elderly cert (2/3/4) → ERROR
 *  3. Age < 65 + 後期高齢 (type 2) → CONFIRM
 *  4. 後期高齢 + card_number ≠ 8 digits → ERROR
 *  5. 後期高齢 + relationship ≠ 本人(1) → ERROR
 *  6. 後期高齢 + insurer not "39xx" → ERROR
 *  7. Relationship=本人(3) + insurer not "67xx" → ERROR
 *  8. Pre-school child cost rate mismatch → WARNING and stop
 *  9. Elderly cert + qual_date < 2006/10/01 + 3割 → ERROR
 * 10. Elderly cert + qual_date 2006~2014 + 2割 → ERROR
 * 11. Age<70@2014 + qual_date ≥ 2014/04/02 + 1割 → ERROR
 * 12. Medical aid type changed from original → ERROR
 * 13. Age ≥ 75 + elderly cert (2/3/4) → CONFIRM
 * 14. Age ≥ 75 + not 後期高齢 → CONFIRM
 * 15. 70 ≤ Age < 75 + no elderly cert → CONFIRM
 *
 * Two types of validations:
 * - ERRORS: Show message dialog, validation fails (returns false)
 * - WARNINGS: Show message dialog, validation fails after OK for blocking rules
 * - CONFIRMS: Ask for confirmation, can proceed if user confirms
 */

import { useCallback } from 'react';
import { UseFormWatch } from 'react-hook-form';
import {
  PatientFormSchemaType,
  validateAgeForElderlyCertificate,
  validateInsurerNumberPrefix,
  validateCardNumberForElderly,
} from '../types';
import useAppModal from '@/hooks/useAppModal';
import { calculateAge } from '@utils/index';
import {
  AGE_EARLY_ELDERLY_NOT_ALLOWED_AT_75_PLUS_CONFIRM,
  AGE_70_TO_74_EARLY_ELDERLY_REQUIRED_CONFIRM,
  BIRTH_DATE_ERROR,
  LATE_ELDERLY_INSURANCE_UNDER_65_CONFIRM,
  MEDICAL_AID_NUMBER_TYPE_ERROR,
  OVER_75_MUST_SET_LATE_ELDERLY_CONFIRM,
  PATIENT_COST_PRESCHOOL_RATE_CHANGE_WARNING,
} from '../constants/validationMessages';

interface UseCrossSectionValidationProps {
  watch: UseFormWatch<PatientFormSchemaType>;
  /** Original medical aid insurer_type_id from loaded patient data (for A2 step 12). */
  originalMedicalAidInsurerTypeId?: number | null;
}

export type CrossSectionValidationResult =
  | { canProceed: true }
  | { canProceed: false; reason: 'error' | 'cancelled' };

export const useCrossSectionValidation = ({
  watch,
  originalMedicalAidInsurerTypeId,
}: UseCrossSectionValidationProps) => {
  const appModal = useAppModal();

  /**
   * Show error dialog (user clicks OK and validation fails)
   */
  const showErrorDialog = useCallback(
    (title: string, content: string = ''): Promise<boolean> => {
      return new Promise(resolve => {
        appModal.error({
          title,
          content,
          okText: 'OK',
          onOk() {
            resolve(false); // Return false - validation failed
          },
        });
      });
    },
    [appModal]
  );

  /**
   * Show warning dialog with confirmation (user can choose to proceed or cancel)
   */
  const showWarning = useCallback(
    (title: string, content: string = '', okText = 'OK'): Promise<void> => {
      return new Promise(resolve => {
        appModal.warning({
          title,
          content,
          okText,
          onOk() {
            resolve();
          },
        });
      });
    },
    [appModal]
  );

  const showWarningConfirm = useCallback(
    (
      title: string,
      content: string,
      okText = '続行',
      cancelText = 'キャンセル'
    ): Promise<boolean> => {
      return new Promise(resolve => {
        appModal.confirm({
          title,
          content,
          okText,
          cancelText,
          onOk() {
            resolve(true); // User confirmed - allow proceeding
          },
          onCancel() {
            resolve(false); // User cancelled
          },
        });
      });
    },
    [appModal]
  );

  // ================================================================
  // Individual validation functions (kept for external use if needed)
  // ================================================================

  // ================================================================
  // Main validation runner — A2 steps 1-15 in exact order
  // ================================================================

  /**
   * Run all cross-section validations matching A2 #btn-update order.
   * Returns canProceed=false with reason=cancelled when the user selects
   * 「いいえ」 on a confirmation dialog.
   */
  const runAllValidations = useCallback(async (): Promise<CrossSectionValidationResult> => {
    const dateOfBirth = watch('patient_information.date_of_birth');
    const insuranceTypeId = watch('health_insurance_card.insurance_type_id');
    const elderlyInsuranceCertificateId = watch(
      'health_insurance_card.elderly_insurance_certificate_id'
    );
    const cardNumber = watch('health_insurance_card.card_number');
    const relationshipId = watch('health_insurance_card.relationship_id');
    const insurerNumber = watch('health_insurance_card.insurer_number');
    const patientCostId = watch('health_insurance_card.patient_cost_id');
    const currentMedicalAidInsurerTypeId = watch('medical_aid.insurer_type_id');
    const medicalAidNumber = watch('medical_aid.medical_aid_number');

    const age = calculateAge(dateOfBirth);

    // ── Step 1: Age < 0 (future birth date) ──
    // A2: #184 生年月日に未来の年月日が入力
    if (age !== null && age !== undefined && age < 0) {
      await showErrorDialog(BIRTH_DATE_ERROR);
      return { canProceed: false, reason: 'error' };
    }

    // ── Step 2: Age < 70 + elderly cert (2/3/4) ──
    // A2: No55 70歳未満で高齢受給者証を選択
    if (age !== null && age !== undefined) {
      const ageValidation = validateAgeForElderlyCertificate(age, elderlyInsuranceCertificateId);
      if (!ageValidation.isValid) {
        await showErrorDialog(ageValidation.message!);
        return { canProceed: false, reason: 'error' };
      }
    }

    // ── Step 3: Age < 65 + 後期高齢 (type 2) ──
    // A2: No206 後期高齢で65歳以下の場合は確認
    if (age !== null && age !== undefined && age < 65 && insuranceTypeId === 2) {
      const proceed = await showWarningConfirm(
        '確認',
        LATE_ELDERLY_INSURANCE_UNDER_65_CONFIRM,
        'はい',
        'いいえ'
      );
      if (!proceed) return { canProceed: false, reason: 'cancelled' };
    }

    // ── Step 4: 後期高齢 + card_number ≠ 8 digits ──
    // A2: No57 後期高齢の場合、被保険者番号は8桁のみ
    if (insuranceTypeId === 2) {
      const cardValidation = validateCardNumberForElderly(insuranceTypeId, cardNumber || '');
      if (!cardValidation.isValid) {
        await showErrorDialog(cardValidation.message!);
        return { canProceed: false, reason: 'error' };
      }
    }

    // ── Step 5: 後期高齢 + relationship ≠ 本人(1) ──
    // A2: No893 後期高齢は続柄を本人のみ
    // ── Step 6: 後期高齢 + insurer not "39xx" ──
    // A2: No142 後期高齢39から始まる番号じゃない場合
    if (insuranceTypeId === 2) {
      const insurerValidation = validateInsurerNumberPrefix(
        relationshipId || null,
        insuranceTypeId,
        insurerNumber || ''
      );
      if (!insurerValidation.isValid) {
        await showErrorDialog(insurerValidation.message!);
        return { canProceed: false, reason: 'error' };
      }
    }

    // ── Step 7: Relationship=本人(3) + insurer not "67xx" ──
    // A2: No899 67（本人）は保険者番号67～番号のみ
    if (insuranceTypeId !== 2 && relationshipId && insurerNumber) {
      const rel67Validation = validateInsurerNumberPrefix(
        relationshipId,
        insuranceTypeId || null,
        insurerNumber
      );
      if (!rel67Validation.isValid) {
        await showErrorDialog(rel67Validation.message!);
        return { canProceed: false, reason: 'error' };
      }
    }

    // ── Step 8: Pre-school child cost rate mismatch ──
    // A2: No95-5 未就学で2割を選択してない場合
    if (dateOfBirth && patientCostId !== undefined && patientCostId !== null) {
      const childAge = calculateAge(dateOfBirth);
      if (childAge !== null) {
        const isSixYearOldRate = patientCostId === 1; // 6歳以下乳幼児2割

        if ((childAge < 6 && !isSixYearOldRate) || (childAge >= 6 && isSixYearOldRate)) {
          await showWarning(PATIENT_COST_PRESCHOOL_RATE_CHANGE_WARNING, '', 'OK');
          return { canProceed: false, reason: 'cancelled' };
        }
      }
    }

    // ── Step 9-11: Qualification date + elderly cert cost rate rules ──
    // A2: No95-6, No413, No389/No894
    // These checks are not required for current validation flow.
    // if (qualificationDate && elderlyInsuranceCertificateId && dateOfBirth) {
    //   const qualDateRules = validateQualificationDateRules(
    //     qualificationDate,
    //     elderlyInsuranceCertificateId,
    //     String(dateOfBirth)
    //   );
    //   if (!qualDateRules.isValid) {
    //     await showErrorDialog(qualDateRules.message!);
    //     return false;
    //   }
    // }

    // ── Step 12: Medical aid type changed from original ──
    // A2: NO122 医療助成入力補助入力で番号選択した後に種別を選択しなおして登録
    if (
      originalMedicalAidInsurerTypeId !== undefined &&
      originalMedicalAidInsurerTypeId !== null &&
      currentMedicalAidInsurerTypeId !== 0 &&
      originalMedicalAidInsurerTypeId !== 0 &&
      medicalAidNumber &&
      medicalAidNumber.trim() !== '' &&
      originalMedicalAidInsurerTypeId !== currentMedicalAidInsurerTypeId
    ) {
      await showErrorDialog(MEDICAL_AID_NUMBER_TYPE_ERROR);
      return { canProceed: false, reason: 'error' };
    }

    // ── Step 13: Age ≥ 75 + elderly cert (2/3/4) → CONFIRM ──
    // A2: 75歳以上にもかかわらず前期高齢者を設定している場合
    if (
      age !== null &&
      age !== undefined &&
      age >= 75 &&
      elderlyInsuranceCertificateId &&
      [2, 3, 4].includes(elderlyInsuranceCertificateId)
    ) {
      const proceed = await showWarningConfirm(
        '年齢確認',
        AGE_EARLY_ELDERLY_NOT_ALLOWED_AT_75_PLUS_CONFIRM,
        'はい',
        'いいえ'
      );
      if (!proceed) return { canProceed: false, reason: 'cancelled' };
    }

    // ── Step 14: Age ≥ 75 + not 後期高齢 → CONFIRM ──
    // A2: 75歳以上で後期高齢者を設定していない場合
    if (age !== null && age !== undefined && age >= 75 && insuranceTypeId !== 2) {
      const proceed = await showWarningConfirm(
        '確認',
        OVER_75_MUST_SET_LATE_ELDERLY_CONFIRM,
        'はい',
        'いいえ'
      );
      if (!proceed) return { canProceed: false, reason: 'cancelled' };
    }

    // ── Step 15: 70 ≤ Age < 75 + no elderly cert → CONFIRM ──
    // A2: No64 70歳以上75歳未満、前期高齢者を設定していない場合
    if (
      age !== null &&
      age !== undefined &&
      age >= 70 &&
      age < 75 &&
      (!elderlyInsuranceCertificateId || elderlyInsuranceCertificateId === 1)
    ) {
      const proceed = await showWarningConfirm(
        '確認',
        AGE_70_TO_74_EARLY_ELDERLY_REQUIRED_CONFIRM,
        'はい',
        'いいえ'
      );
      if (!proceed) return { canProceed: false, reason: 'cancelled' };
    }

    return { canProceed: true };
  }, [watch, showErrorDialog, showWarning, showWarningConfirm, originalMedicalAidInsurerTypeId]);

  return {
    runAllValidations,
  };
};
